import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, FileText, Calendar, Database, Headphones } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Library Document Detail
 * Reads a single archived document and renders its full text.
 */
export default function LibraryDocumentPage() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API_URL}/api/library/documents/${encodeURIComponent(docId)}`);
        if (!cancelled) {
          if (r.status === 404) setNotFound(true);
          else if (r.ok) setDoc(await r.json());
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [docId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400" />
      </div>
    );
  }

  if (notFound || !doc) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center max-w-md">
          <FileText className="w-12 h-12 text-amber-300/40 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Document not found</h1>
          <p className="text-gray-400 mb-6">This entry isn't in the Library yet.</p>
          <button
            onClick={() => navigate('/library')}
            data-testid="library-doc-back"
            className="bg-amber-400 hover:bg-amber-300 text-black px-6 py-3 rounded-lg font-semibold"
          >
            Back to Library
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="library-doc-page">
      {/* Header */}
      <header className="border-b border-amber-500/30 bg-gradient-to-b from-amber-950/30 to-black">
        <div className="container mx-auto px-4 py-6 flex flex-wrap items-center justify-between gap-4">
          <Link
            to="/library"
            data-testid="library-doc-back-link"
            className="text-amber-200/80 hover:text-amber-200 flex items-center gap-2 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Library
          </Link>
          <nav className="flex items-center gap-2" aria-label="Knowledge Gateway wings">
            <Link
              to="/"
              className="px-4 py-2 rounded-full text-sm font-mono uppercase tracking-[0.18em] border border-purple-400/50 text-purple-200 hover:bg-purple-500/10 transition-colors"
            >
              University
            </Link>
            <span className="px-4 py-2 rounded-full text-sm font-mono uppercase tracking-[0.18em] border-2 border-amber-300 bg-amber-300/10 text-amber-200">
              Library
            </span>
          </nav>
        </div>
      </header>

      <article className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="text-xs font-mono uppercase tracking-[0.22em] text-amber-300/70 mb-3">
          Library Document
        </div>
        <h1
          data-testid="library-doc-title"
          className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4"
          style={{ textShadow: '0 0 28px rgba(252, 211, 77, 0.18)' }}
        >
          {doc.title}
        </h1>

        <div className="flex flex-wrap items-center gap-3 text-xs font-mono text-gray-400 mb-8">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
            <Database className="w-3 h-3 text-amber-300/70" />
            {doc.source_node || 'scholar'}
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
            <FileText className="w-3 h-3 text-amber-300/70" />
            {doc.char_count?.toLocaleString() || 0} chars
          </span>
          {doc.ingested_at && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
              <Calendar className="w-3 h-3 text-amber-300/70" />
              {new Date(doc.ingested_at).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
            </span>
          )}
          {doc.gift_type && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-300/10 border border-amber-300/30 text-amber-200">
              gift · {doc.gift_type}
            </span>
          )}
          {doc.audio_url && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cyan-400/10 border border-cyan-300/40 text-cyan-200">
              <Headphones className="w-3 h-3" />
              Audio companion
            </span>
          )}
        </div>

        {/* Audio companion player — surfaces whenever the doc has an audio_url */}
        {doc.audio_url && (
          <section
            data-testid="library-doc-audio"
            className="mb-10 rounded-2xl border border-cyan-400/30 bg-gradient-to-br from-cyan-950/40 via-gray-900 to-black p-5 md:p-6 shadow-[0_0_30px_rgba(34,211,238,0.08)]"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-cyan-400/15 border border-cyan-300/40 flex items-center justify-center">
                <Headphones className="w-5 h-5 text-cyan-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-cyan-300/80">
                  Audio companion
                </div>
                <div className="text-sm font-semibold text-white truncate" title={doc.audio_title || doc.title}>
                  {doc.audio_title || doc.title}
                </div>
              </div>
              <a
                href={doc.audio_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="library-doc-audio-download"
                className="text-[10px] font-mono uppercase tracking-[0.18em] text-cyan-300/80 hover:text-cyan-200 border border-cyan-300/30 hover:border-cyan-300/60 rounded-full px-3 py-1 transition-colors whitespace-nowrap"
              >
                Open ↗
              </a>
            </div>
            <audio
              data-testid="library-doc-audio-player"
              controls
              preload="metadata"
              src={doc.audio_url}
              className="w-full"
              style={{ accentColor: '#22d3ee' }}
            >
              Your browser doesn&apos;t support inline audio.{' '}
              <a href={doc.audio_url} target="_blank" rel="noopener noreferrer" className="text-cyan-300 underline">
                Open the audio in a new tab.
              </a>
            </audio>
          </section>
        )}

        {doc.content ? (
          <div
            data-testid="library-doc-content"
            className="prose prose-invert max-w-none text-gray-200 leading-relaxed whitespace-pre-wrap font-sans"
            style={{ fontSize: '15px', lineHeight: 1.75 }}
          >
            {doc.content}
          </div>
        ) : (
          <div className="rounded-xl border border-amber-300/20 bg-amber-950/10 p-6 text-gray-400">
            This entry is a seed record — title only, no content body yet. Drop in via the Wire Protocol
            <code className="text-amber-300 mx-1">POST /api/federation/gift</code>
            with <code className="text-amber-300 mx-1">type=library_doc</code> and a <code className="text-amber-300 mx-1">content</code> field, and Xavier will steward it.
          </div>
        )}
      </article>
    </div>
  );
}
